import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField";
import Button from "components/CustomButtons/Button.jsx";

const SYNC_MODE_OPTIONS = [
  { value: "on_read", label: "On Read" },
  { value: "periodic", label: "Periodic" },
  { value: "manual", label: "Manual" }
];

export default function BindingEditor({ assets, devices, onSave, isSaving }) {
  const [form, setForm] = useState({
    name: "",
    asset: "",
    source_type: "device_status",
    source_asset: "",
    sourceDeviceIds: [],
    sync_mode: "on_read",
    active: true,
    sourceKey: "",
    targetKey: "",
    transform: ""
  });

  const assetOptions = useMemo(
    () => (assets || []).map(item => ({ value: item.id, text: item.name })),
    [assets]
  );

  const deviceOptions = useMemo(
    () =>
      (devices || []).map(item => ({
        value: String(item.id),
        text: item.alias || item.name || item.ip_address || String(item.id)
      })),
    [devices]
  );

  const selectedDeviceLabels = useMemo(() => {
    const byId = new Map(deviceOptions.map(item => [item.value, item.text]));
    return (form.sourceDeviceIds || []).map(deviceId => byId.get(String(deviceId)) || String(deviceId));
  }, [deviceOptions, form.sourceDeviceIds]);

  const onDeviceSelectionChange = event => {
    const selectedValues = Array.from(event.target.options)
      .filter(option => option.selected)
      .map(option => option.value);
    setForm({ ...form, sourceDeviceIds: selectedValues });
  };

  const save = () => {
    if (!form.name || !form.asset) {
      return;
    }

    const mappingRule =
      form.sourceKey && form.targetKey
        ? [
            {
              source_key: form.sourceKey,
              target_key: form.targetKey,
              transform: form.transform || null
            }
          ]
        : [];

    const payload = {
      name: form.name,
      asset: form.asset,
      source_type: form.source_type,
      source_asset:
        form.source_type === "asset_attribute" && form.source_asset
          ? form.source_asset
          : null,
      devices:
        form.source_type === "device_status"
          ? form.sourceDeviceIds.map(item => String(item))
          : [],
      sync_mode: form.sync_mode,
      active: form.active,
      attribute_mappings: mappingRule,
      attribute_mapping_template:
        form.sourceKey && form.targetKey
          ? { [form.targetKey]: form.sourceKey }
          : {}
    };

    onSave(payload).then(() => {
      setForm({
        name: "",
        asset: "",
        source_type: "device_status",
        source_asset: "",
        sourceDeviceIds: [],
        sync_mode: "on_read",
        active: true,
        sourceKey: "",
        targetKey: "",
        transform: ""
      });
    });
  };

  return (
    <div style={{ display: "grid", gap: 12, marginBottom: 20 }}>
      <TextField
        label="Agent Name"
        variant="outlined"
        size="small"
        value={form.name}
        onChange={e => setForm({ ...form, name: e.target.value })}
      />
      <TextField
        select
        label="Asset"
        variant="outlined"
        size="small"
        SelectProps={{ native: true }}
        value={form.asset}
        onChange={e => setForm({ ...form, asset: e.target.value })}
      >
        <option value="">Select an asset</option>
        {assetOptions.map(item => (
          <option key={item.value} value={item.value}>
            {item.text}
          </option>
        ))}
      </TextField>
      <TextField
        select
        label="Source Type"
        variant="outlined"
        size="small"
        SelectProps={{ native: true }}
        value={form.source_type}
        onChange={e =>
          setForm({
            ...form,
            source_type: e.target.value,
            source_asset: "",
            sourceDeviceIds: []
          })
        }
      >
        <option value="device_status">Device Status</option>
        <option value="asset_attribute">Asset Attribute</option>
      </TextField>
      {form.source_type === "asset_attribute" ? (
        <TextField
          select
          label="Source Asset"
          variant="outlined"
          size="small"
          SelectProps={{ native: true }}
          value={form.source_asset}
          onChange={e => setForm({ ...form, source_asset: e.target.value })}
        >
          <option value="">Select source asset</option>
          {assetOptions.map(item => (
            <option key={item.value} value={item.value}>
              {item.text}
            </option>
          ))}
        </TextField>
      ) : (
        <>
          <TextField
            select
            label="Source Devices"
            variant="outlined"
            size="small"
            SelectProps={{
              native: true,
              multiple: true,
              onChange: onDeviceSelectionChange
            }}
            value={form.sourceDeviceIds}
            helperText="Only devices you can access are listed"
          >
            {deviceOptions.length === 0 ? (
              <option disabled value="">
                No accessible devices found
              </option>
            ) : null}
            {deviceOptions.map(item => (
              <option key={item.value} value={item.value}>
                {item.text}
              </option>
            ))}
          </TextField>
          {selectedDeviceLabels.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {selectedDeviceLabels.map(label => (
                <span
                  key={label}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    background: "#e0f2fe",
                    color: "#0c4a6e",
                    border: "1px solid #bae6fd"
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          ) : null}
        </>
      )}
      <TextField
        select
        label="Sync Mode"
        variant="outlined"
        size="small"
        SelectProps={{ native: true }}
        value={form.sync_mode}
        onChange={e => setForm({ ...form, sync_mode: e.target.value })}
      >
        {SYNC_MODE_OPTIONS.map(item => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </TextField>
      <TextField
        label="Source Key"
        variant="outlined"
        size="small"
        helperText={
          form.source_type === "asset_attribute"
            ? "Attribute key on source asset"
            : "Device status field key"
        }
        value={form.sourceKey}
        onChange={e => setForm({ ...form, sourceKey: e.target.value })}
      />
      <TextField
        label="Target Key"
        variant="outlined"
        size="small"
        helperText="Destination attribute key on selected asset"
        value={form.targetKey}
        onChange={e => setForm({ ...form, targetKey: e.target.value })}
      />
      <TextField
        label="Transform (optional)"
        variant="outlined"
        size="small"
        value={form.transform}
        onChange={e => setForm({ ...form, transform: e.target.value })}
      />
      <Button color="info" onClick={save} disabled={isSaving}>
        {isSaving ? "Saving..." : "Create Binding Agent"}
      </Button>
    </div>
  );
}

BindingEditor.propTypes = {
  assets: PropTypes.array,
  devices: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool
};
